<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import PlaygroundTab from './PlaygroundTab.vue';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();
const alert = useAlert;
const form = ref({});
const newExpression = ref('');
const newHandoffRule = ref('');

const resetForm = () => {
  const cloned = JSON.parse(JSON.stringify(props.agent));
  form.value = {
    ...cloned,
    response_level: cloned.response_level ?? 3,
    config: {
      common_expressions: [],
      retargeting_enabled: false,
      retargeting_hours: 24,
      retargeting_message: '',
      inactivity_enabled: false,
      inactivity_minutes: 5,
      inactivity_use_ai: false,
      inactivity_message: '',
      follow_ups_enabled: false,
      follow_ups_count: 2,
      follow_ups_interval_minutes: 10,
      daily_follow_up_limit: 30,
      follow_up_start_hour: 9,
      follow_up_end_hour: 21,
      emojis_support: false,
      response_tone: '',
      key_information: '',
      avoid_doing: '',
      handoff_enabled: false,
      handoff_rules: [],
      flow_steps: [],
      ...cloned.config,
    },
  };
};

watch(() => props.agent, resetForm, { immediate: true, deep: true });

const addExpression = () => {
  const text = newExpression.value.trim();
  if (!text) return;
  if (!form.value.config.common_expressions) {
    form.value.config.common_expressions = [];
  }
  form.value.config.common_expressions.push(text);
  newExpression.value = '';
};

const removeExpression = index => {
  form.value.config.common_expressions.splice(index, 1);
};

const addHandoffRule = () => {
  const text = newHandoffRule.value.trim();
  if (!text) return;
  if (!form.value.config.handoff_rules) {
    form.value.config.handoff_rules = [];
  }
  form.value.config.handoff_rules.push(text);
  newHandoffRule.value = '';
};

const removeHandoffRule = index => {
  form.value.config.handoff_rules.splice(index, 1);
};

const save = async () => {
  try {
    await store.dispatch('aiAgents/update', {
      id: props.agent.id,
      data: form.value,
    });
    alert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    emit('saved');
  } catch (error) {
    alert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  }
};
</script>

<template>
  <div class="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-6 items-start">
    <div class="grid grid-cols-1 gap-6">
      <!-- SECCIÓN: IDENTIDAD -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div class="xl:col-span-2 flex items-center gap-2 mb-1">
          <span class="i-lucide-sparkles w-4 h-4 text-n-brand" />
          <span
            class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.BEHAVIOR.SECTION_IDENTITY') }}
          </span>
          <div class="flex-1 h-px bg-n-slate-6" />
        </div>

        <Accordion
          icon="i-lucide-user"
          :title="t('AI_AGENTS.BEHAVIOR.PERSONALIZATION_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.PERSONALIZATION_DESCRIPTION')"
          is-open
        >
          <textarea
            v-model="form.system_prompt"
            rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            :placeholder="t('AI_AGENTS.BEHAVIOR.PERSONALIZATION_PLACEHOLDER')"
          />
        </Accordion>

        <Accordion
          icon="i-lucide-target"
          :title="t('AI_AGENTS.BEHAVIOR.OBJECTIVE_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.OBJECTIVE_DESCRIPTION')"
          is-open
        >
          <textarea
            v-model="form.instructions"
            rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            :placeholder="t('AI_AGENTS.BEHAVIOR.OBJECTIVE_PLACEHOLDER')"
          />
        </Accordion>

        <Accordion
          icon="i-lucide-info"
          :title="t('AI_AGENTS.BEHAVIOR.KEY_INFO_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.KEY_INFO_DESCRIPTION')"
        >
          <label class="grid gap-1 mb-3">
            <span class="text-sm font-medium">
              {{ t('AI_AGENTS.BEHAVIOR.KEY_INFO_DO_LABEL') }}
            </span>
            <textarea
              v-model="form.config.key_information"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.BEHAVIOR.KEY_INFO_DO_PLACEHOLDER')"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-sm font-medium">
              {{ t('AI_AGENTS.BEHAVIOR.KEY_INFO_DONT_LABEL') }}
            </span>
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('AI_AGENTS.BEHAVIOR.KEY_INFO_DONT_DESCRIPTION') }}
            </p>
            <textarea
              v-model="form.config.avoid_doing"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.BEHAVIOR.KEY_INFO_DONT_PLACEHOLDER')"
            />
          </label>
        </Accordion>

        <Accordion
          icon="i-lucide-message-circle"
          :title="t('AI_AGENTS.BEHAVIOR.WELCOME_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.WELCOME_DESCRIPTION')"
        >
          <textarea
            v-model="form.welcome_message"
            rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            :placeholder="t('AI_AGENTS.BEHAVIOR.WELCOME_PLACEHOLDER')"
          />
        </Accordion>
      </div>

      <!-- SECCIÓN: RESPUESTA -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div class="xl:col-span-2 flex items-center gap-2 mb-1">
          <span class="i-lucide-message-square w-4 h-4 text-n-amber-9" />
          <span
            class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.BEHAVIOR.SECTION_RESPONSE') }}
          </span>
          <div class="flex-1 h-px bg-n-slate-6" />
        </div>

        <Accordion
          icon="i-lucide-sliders-horizontal"
          :title="t('AI_AGENTS.BEHAVIOR.RESPONSE_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.RESPONSE_DESCRIPTION')"
        >
          <div class="grid gap-4">
            <label class="grid gap-2">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.RESPONSE_LEVEL_LABEL') }}
              </span>
              <input
                v-model.number="form.response_level"
                type="range"
                min="1"
                max="5"
                step="1"
                class="w-full h-2 rounded-lg appearance-none cursor-pointer bg-n-slate-6 accent-n-brand"
              />
              <div class="flex justify-between text-xs text-n-slate-10">
                <span>{{ $t('AI_AGENTS.BEHAVIOR.LEVEL_MIN') }}</span>
                <span>{{ $t('AI_AGENTS.BEHAVIOR.LEVEL_MAX') }}</span>
              </div>
              <span class="text-xs text-n-slate-10">
                {{ form.response_level }}
              </span>
            </label>

            <label class="flex items-center gap-2">
              <input
                v-model="form.config.emojis_support"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.EMOJIS_LABEL') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.TONE_LABEL') }}
              </span>
              <textarea
                v-model="form.config.response_tone"
                rows="3"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="t('AI_AGENTS.BEHAVIOR.TONE_PLACEHOLDER')"
              />
            </label>

            <div class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.EXPRESSIONS_LABEL') }}
              </span>
              <div class="flex gap-2">
                <input
                  v-model="newExpression"
                  type="text"
                  class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                  :placeholder="t('AI_AGENTS.BEHAVIOR.EXPRESSION_PLACEHOLDER')"
                  @keyup.enter="addExpression"
                />
                <button
                  class="px-3 py-2 text-xs font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
                  @click="addExpression"
                >
                  {{ t('AI_AGENTS.BEHAVIOR.ADD_EXPRESSION') }}
                </button>
              </div>
              <div class="flex flex-wrap gap-2 mt-1">
                <div
                  v-for="(expr, index) in form.config.common_expressions"
                  :key="index"
                  class="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-n-brand/10 text-n-brand border border-n-brand/20"
                >
                  {{ expr }}
                  <button
                    class="text-n-brand/60 hover:text-n-ruby-9"
                    @click="removeExpression(index)"
                  >
                    <span class="i-lucide-x w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Accordion>
      </div>

      <!-- SECCIÓN: AUTOMATIZACIÓN -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div class="xl:col-span-2 flex items-center gap-2 mb-1">
          <span class="i-lucide-zap w-4 h-4 text-n-ruby-9" />
          <span
            class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.BEHAVIOR.SECTION_AUTOMATION') }}
          </span>
          <div class="flex-1 h-px bg-n-slate-6" />
        </div>

        <Accordion
          icon="i-lucide-refresh-cw"
          :title="t('AI_AGENTS.BEHAVIOR.RETARGETING_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.RETARGETING_DESCRIPTION')"
        >
          <div class="grid gap-4">
            <label class="flex items-center gap-2">
              <input
                v-model="form.config.retargeting_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.RETARGETING_ENABLED') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.RETARGETING_HOURS_LABEL') }}
              </span>
              <input
                v-model.number="form.config.retargeting_hours"
                type="number"
                min="0"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-24"
              />
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.RETARGETING_MESSAGE_LABEL') }}
              </span>
              <textarea
                v-model="form.config.retargeting_message"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.BEHAVIOR.RETARGETING_MESSAGE_PLACEHOLDER')
                "
              />
            </label>
          </div>
        </Accordion>

        <Accordion
          icon="i-lucide-clock"
          :title="t('AI_AGENTS.BEHAVIOR.INACTIVITY_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.INACTIVITY_DESCRIPTION')"
        >
          <div class="grid gap-4">
            <label class="flex items-center gap-2">
              <input
                v-model="form.config.inactivity_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.INACTIVITY_ENABLED') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.INACTIVITY_MINUTES_LABEL') }}
              </span>
              <input
                v-model.number="form.config.inactivity_minutes"
                type="number"
                min="1"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-24"
              />
            </label>

            <label class="flex items-center gap-2">
              <input
                v-model="form.config.inactivity_use_ai"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.INACTIVITY_USE_AI') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.INACTIVITY_MESSAGE_LABEL') }}
              </span>
              <textarea
                v-model="form.config.inactivity_message"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.BEHAVIOR.INACTIVITY_MESSAGE_PLACEHOLDER')
                "
              />
            </label>

            <label class="flex items-center gap-2">
              <input
                v-model="form.config.follow_ups_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.FOLLOW_UPS_ENABLED') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.FOLLOW_UPS_COUNT_LABEL') }}
              </span>
              <input
                v-model.number="form.config.follow_ups_count"
                type="number"
                min="1"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-24"
              />
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.FOLLOW_UPS_INTERVAL_LABEL') }}
              </span>
              <input
                v-model.number="form.config.follow_ups_interval_minutes"
                type="number"
                min="1"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-24"
              />
            </label>

            <div class="border-t border-n-slate-6 pt-3 mt-1">
              <span class="text-sm font-medium text-n-slate-11">
                {{ t('AI_AGENTS.BEHAVIOR.ANTI_SPAM_TITLE') }}
              </span>
              <p class="text-xs text-n-slate-10 mt-0.5">
                {{ t('AI_AGENTS.BEHAVIOR.ANTI_SPAM_DESCRIPTION') }}
              </p>
            </div>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.DAILY_LIMIT_LABEL') }}
              </span>
              <input
                v-model.number="form.config.daily_follow_up_limit"
                type="number"
                min="1"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-24"
              />
            </label>

            <div class="grid grid-cols-2 gap-3">
              <label class="grid gap-1">
                <span class="text-sm font-medium">
                  {{ t('AI_AGENTS.BEHAVIOR.START_HOUR_LABEL') }}
                </span>
                <input
                  v-model.number="form.config.follow_up_start_hour"
                  type="number"
                  min="0"
                  max="23"
                  class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-20"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-sm font-medium">
                  {{ t('AI_AGENTS.BEHAVIOR.END_HOUR_LABEL') }}
                </span>
                <input
                  v-model.number="form.config.follow_up_end_hour"
                  type="number"
                  min="1"
                  max="24"
                  class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-20"
                />
              </label>
            </div>
          </div>
        </Accordion>

        <Accordion
          icon="i-lucide-user-check"
          :title="t('AI_AGENTS.BEHAVIOR.HANDOFF_TITLE')"
          :description="t('AI_AGENTS.BEHAVIOR.HANDOFF_DESCRIPTION')"
        >
          <div class="grid gap-4">
            <label class="flex items-center gap-2">
              <input
                v-model="form.config.handoff_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border border-n-slate-6"
              />
              <span class="text-sm">
                {{ t('AI_AGENTS.BEHAVIOR.HANDOFF_ENABLED') }}
              </span>
            </label>

            <label class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.HANDOFF_MESSAGE_LABEL') }}
              </span>
              <textarea
                v-model="form.handoff_message"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.BEHAVIOR.HANDOFF_MESSAGE_PLACEHOLDER')
                "
              />
            </label>

            <div class="grid gap-1">
              <span class="text-sm font-medium">
                {{ t('AI_AGENTS.BEHAVIOR.HANDOFF_RULES_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-10">
                {{ t('AI_AGENTS.BEHAVIOR.HANDOFF_RULES_DESCRIPTION') }}
              </p>
              <div class="flex gap-2">
                <input
                  v-model="newHandoffRule"
                  type="text"
                  class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                  :placeholder="
                    t('AI_AGENTS.BEHAVIOR.HANDOFF_RULE_PLACEHOLDER')
                  "
                  @keyup.enter="addHandoffRule"
                />
                <button
                  class="px-3 py-2 text-xs font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
                  @click="addHandoffRule"
                >
                  {{ t('AI_AGENTS.BEHAVIOR.ADD_HANDOFF_RULE') }}
                </button>
              </div>
              <div class="flex flex-wrap gap-2 mt-1">
                <div
                  v-for="(rule, index) in form.config.handoff_rules"
                  :key="index"
                  class="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-n-slate-4 text-n-slate-12"
                >
                  {{ rule }}
                  <button
                    class="text-n-slate-10 hover:text-n-ruby-9"
                    @click="removeHandoffRule(index)"
                  >
                    <span class="i-lucide-x w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Accordion>
      </div>

      <div class="flex justify-end">
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
          @click="save"
        >
          {{ t('AI_AGENTS.FORM.SAVE') }}
        </button>
      </div>
    </div>

    <PlaygroundTab :agent="agent" />
  </div>
</template>
